import {
  Document,
  Image,
  Line,
  Page,
  Path,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer'
import React from 'react'
import { Html } from 'react-pdf-html'
import { tw } from '~/components/reports'
import { TYPES } from '~/constants'
import { cls, fmtAddress } from '~/lib'
import type { ReportPdfOptions } from '~/models'
import { Client, Listing } from '~/models'
import PdfBadge from './badge'
import PdfListingBrokers from './brokers'
import PdfLeaseSpaces from './lease-spaces'
import PdfListingMeta from './listing-meta'
import {
  PdfDisclaimersPage,
  PdfImagesPage,
  ReportPdfMapPage,
  ReportPdfTitlePage,
} from './pages'
import { getReportListingName } from './utils'

export function getMaxSubstringLength(value: string, maxLength: number) {
  if (value.length <= maxLength) return value.length
  const lastSpaceIndex = value.lastIndexOf(' ', maxLength)
  if (lastSpaceIndex === -1) return maxLength
  return lastSpaceIndex
}

const LucideImageOffSvg = ({
  width,
  height,
}: {
  width: number
  height: number
}) => (
  <Svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    stroke="black"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <Line x1="2" x2="22" y1="2" y2="22" />
    <Path d="M10.41 10.41a2 2 0 1 1-2.83-2.83" />
    <Line x1="13.5" x2="6" y1="13.5" y2="21" />
    <Line x1="18" x2="21" y1="12" y2="15" />
    <Path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.052-.22 1.41-.59" />
    <Path d="M21 15V5a2 2 0 0 0-2-2H9" />
  </Svg>
)

export default function ReportFlyersPdf({
  client,
  title,
  listings,
  pdfOptions,
}: {
  client: Client
  title: string
  listings: Listing[]
  pdfOptions: ReportPdfOptions
}) {
  return (
    <Document title={title}>
      {pdfOptions.showTitlePage && (
        <ReportPdfTitlePage
          title={title}
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
          client={client}
        />
      )}
      {pdfOptions.showMapPage && (
        <ReportPdfMapPage
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {listings.map((listing, index) => {
        return (
          <React.Fragment key={listing.id}>
            <Page id={listing.id} size="LETTER" style={tw('p-8 font-sans')}>
              <View style={tw('font-sans')}>
                <View style={tw('flex flex-row items-start gap-4')}>
                  <Text style={tw('font-heading text-2xl leading-tight')}>
                    {getReportListingName(listing)}
                  </Text>
                </View>
                <Text style={tw('mt-1 font-sans text-lg leading-tight')}>
                  {fmtAddress(listing.property)}
                </Text>
              </View>
              <View style={tw('mt-2 flex flex-row gap-6')}>
                <View style={tw('w-7/12')}>
                  {(listing.images || []).length > 0 ? (
                    <View
                      style={tw(
                        'flex h-[200px] w-[319px] flex-row items-center justify-center rounded-sm bg-slate-100'
                      )}>
                      <Image
                        src={`${listing.images![0].thumbnail_url}?bust=${new Date().getTime()}`}
                      />
                    </View>
                  ) : (
                    <View style={tw('rounded-sm bg-gray-200 pb-[56.25%]')}>
                      <LucideImageOffSvg width={20} height={20} />
                    </View>
                  )}

                  {(listing.meta.description || listing.meta.highlights) && (
                    <View style={tw('mt-4')}>
                      <Text
                        style={tw('font-heading mb-4 text-base leading-none')}>
                        Overview
                      </Text>
                      {listing.meta.description && (
                        <Html
                          style={tw('font-sans text-sm leading-tight')}
                          stylesheet={{
                            p: { margin: '0 0 10px' },
                            li: { margin: 0 },
                            ul: { margin: 0 },
                            'ul > ul': { margin: 0 },
                          }}>
                          {listing.meta.description.length > 1000
                            ? `${listing.meta.description.slice(0, 1000)}...`
                            : listing.meta.description}
                        </Html>
                      )}
                      {listing.meta.highlights && (
                        <Html
                          style={tw('font-sans text-sm leading-tight')}
                          stylesheet={{
                            p: { margin: '0 0 10px' },
                            li: { margin: 0 },
                            ul: { margin: 0 },
                            'ul > ul': { margin: 0 },
                          }}>
                          {listing.meta.highlights}
                        </Html>
                      )}
                    </View>
                  )}

                  <PdfLeaseSpaces listing={listing} />
                </View>

                <View style={tw('w-5/12')}>
                  <View style={tw('mb-2 flex flex-row')}>
                    <PdfBadge
                      className={cls(
                        'py-2 text-white',
                        listing.type === 'sale' && 'bg-[#0284c7]',
                        listing.type === 'lease' && 'bg-[#c026d3]',
                        listing.type === 'sale/lease' && 'bg-[#4338ca]'
                      )}>
                      For {TYPES[listing.type]}
                    </PdfBadge>
                  </View>
                  <PdfListingMeta listing={listing} pdfOptions={pdfOptions} />
                  {pdfOptions.showBrokers && (
                    <PdfListingBrokers listing={listing} client={client} />
                  )}
                </View>
              </View>

              {listings.length > 1 && (
                <View fixed style={tw('absolute right-4 top-4')}>
                  <Text style={tw('font-sans text-sm italic')}>
                    Listing {index + 1} of {listings.length}
                  </Text>
                </View>
              )}
            </Page>

            {pdfOptions.showImages && <PdfImagesPage listing={listing} />}
          </React.Fragment>
        )
      })}
      <PdfDisclaimersPage
        pdfOptions={pdfOptions}
        orientation="portrait"
        client={client}
      />
    </Document>
  )
}
